// Dashboard.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut } from "lucide-react";
import { getRegistrations, downloadExcel } from "./apiservice";

export default function Dashboard() {
  const [registrations, setRegistrations] = useState([]);
  const [limit, setLimit] = useState(200);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please Login");
      navigate("/Admin-login");
      return;
    }
  }, [navigate]);

  const fetchRegistrations = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRegistrations(limit, offset);
      setRegistrations(data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch registrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, [limit, offset]);

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setOffset(0); // Reset offset when limit changes
  };

  const handleNext = () => {
    if (registrations.length === limit) {
      setOffset(offset + limit);
    }
  };

  const handlePrev = () => {
    if (offset > 0) {
      setOffset(offset - limit);
    }
  };

  const handleDownload = async () => {
    try {
      await downloadExcel(limit, offset);
    } catch (err) {
      setError("Failed to download Excel");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/Admin-login");
  };

  const gradientSecondary = {
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
  };

  const cardStyle = {
    width: '100%',
    maxWidth: '96rem',
    margin: '0 auto',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    borderRadius: '0.5rem',
    overflow: 'hidden'
  };

  const cardHeaderStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.5rem'
  };

  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent'
  };

  const controlsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  };

  const selectStyle = {
    width: '180px',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem'
  };

  const buttonStyle = {
    padding: '0.75rem 1rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    opacity: 1,
    transition: 'opacity 0.2s'
  };

  const outlineButtonStyle = {
    ...buttonStyle,
    background: 'transparent',
    color: '#374151',
    border: '1px solid #d1d5db'
  };

  const cardContentStyle = {
    padding: '1.5rem'
  };

  const errorStyle = {
    color: '#ef4444',
    fontSize: '0.875rem',
    marginBottom: '1rem'
  };

  const tableContainerStyle = {
    overflowX: 'auto'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  };

  const thStyle = {
    background: '#f9fafb',
    padding: '0.75rem',
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: '2px solid #e5e7eb',
    whiteSpace: 'nowrap'
  };

  const tdStyle = {
    padding: '0.75rem',
    borderBottom: '1px solid #e5e7eb',
    wordBreak: 'break-word'
  };

  const trStyle = {
    borderBottom: '1px solid #e5e7eb'
  };

  const paginationStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1rem',
    alignItems: 'center'
  };

  const loadingStyle = {
    ...gradientSecondary,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem'
  };

  const loadingCardStyle = {
    width: '100%',
    maxWidth: '28rem'
  };

  const headers = [
    'ID',
    'Name',
    'Email',
    'Phone',
    'Country',
    'Educational Status',
    'Qualification',
    'Passed Out Year',
    'Interest',
    'State',
    'City',
    'College Name',
    'Reference',
    'Created At'
  ];

  const fields = [
    'id',
    'name',
    'email',
    'phone',
    'country',
    'educational_status',
    'qualification',
    'passedout_year',
    'interest',
    'state',
    'city',
    'college_name',
    'reference',
    'created_at'
  ];

  if (loading) {
    return (
      <div style={loadingStyle}>
        <div style={loadingCardStyle}>
          <div style={{ padding: '1.5rem', textAlign: 'center' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...gradientSecondary, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <div>
            <h2 style={titleStyle}>Student Registrations</h2>
          </div>
          <div style={controlsStyle}>
            <select value={limit} onChange={handleLimitChange} style={selectStyle}>
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={1500}>1500</option>
            </select>
            <button onClick={handleDownload} style={buttonStyle} onMouseOver={(e) => e.target.style.opacity = '0.9'} onMouseOut={(e) => e.target.style.opacity = '1'}>
              <Download style={{ width: '1rem', height: '1rem' }} />
              <span>Download Excel</span>
            </button>
            <button onClick={handleLogout} style={outlineButtonStyle}>
              <LogOut style={{ width: '1rem', height: '1rem' }} />
              <span>Logout</span>
            </button>
          </div>
        </div>
        <div style={cardContentStyle}>
          {error && <div style={errorStyle}>{error}</div>}
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr style={trStyle}>
                  {headers.map((header, index) => (
                    <th key={index} style={thStyle}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {registrations.length > 0 ? (
                  registrations.map((reg, index) => (
                    <tr key={index} style={trStyle}>
                      {fields.map((field, fieldIndex) => (
                        <td key={fieldIndex} style={tdStyle}>
                          {reg[field] || 'N/A'}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={headers.length} style={{ ...tdStyle, textAlign: 'center' }}>No registrations found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={paginationStyle}>
            <button onClick={handlePrev} disabled={offset === 0} style={{ ...outlineButtonStyle, opacity: offset === 0 ? 0.5 : 1, cursor: offset === 0 ? 'not-allowed' : 'pointer' }}>
              Previous
            </button>
            <span>
              Showing {offset + 1} to {Math.min(offset + registrations.length)} of {/* total if known */}
            </span>
            <button onClick={handleNext} disabled={registrations.length < limit} style={{ ...outlineButtonStyle, opacity: registrations.length < limit ? 0.5 : 1, cursor: registrations.length < limit ? 'not-allowed' : 'pointer' }}>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}